import { getFreshProductPrices } from './typesense/client/server-client';

export interface PriceUpdateResult {
  success: boolean;
  updatedItems: Array<{
    id: string;
    price: number;
    sale_price: number;
    oldPrice?: number;
    oldSalePrice?: number;
    promotional_pack_unit_price?: number;
    oldPromotionalPackUnitPrice?: number;
    product_prices?: any[];
  }>;
  errors: string[];
}

export interface CartPriceUpdateOptions {
  forceUpdate?: boolean;
  silent?: boolean;
  onProgress?: (progress: number) => void;
}

export class CartPriceUpdateService {
  private static instance: CartPriceUpdateService;
  private lastUpdateTime: Map<string, number> = new Map();
  private updateCooldown = 5 * 60 * 1000; // 5 minutes cooldown between updates for same SKUs

  static getInstance(): CartPriceUpdateService {
    if (!CartPriceUpdateService.instance) {
      CartPriceUpdateService.instance = new CartPriceUpdateService();
    }
    return CartPriceUpdateService.instance;
  }

  /**
   * Check if prices need updating for given SKUs
   */
  private needsUpdate(skus: string[], forceUpdate: boolean = false): boolean {
    if (forceUpdate) return true;
    
    const now = Date.now();
    return skus.some(sku => {
      const lastUpdate = this.lastUpdateTime.get(sku);
      return !lastUpdate || (now - lastUpdate) > this.updateCooldown;
    });
  }

  /**
   * Validates if a product kit promotion can be applied
   * @param cartQuantity Current quantity in cart
   * @param promotionalPackQuantity Required quantity for promotional pack
   * @param availableStock Available stock for the product
   * @returns true if promotion is valid, false otherwise
   */
  private isValidKitPromotion(
    cartQuantity: number,
    promotionalPackQuantity?: number,
    availableStock?: number
  ): boolean {
    if (!promotionalPackQuantity || promotionalPackQuantity <= 0) {
      return false;
    }

    // Check if there's enough stock for the promotional pack
    if (availableStock !== undefined && availableStock < promotionalPackQuantity) {
      return false;
    }

    // Check if cart quantity matches promotional pack requirement (must be multiple of promotional_pack_quantity)
    return cartQuantity % promotionalPackQuantity === 0;
  }

  /**
   * Validates if product has sufficient stock
   * @param cartQuantity Current quantity in cart
   * @param availableStock Available stock for the product
   * @returns true if stock is sufficient, false otherwise
   */
  private hasSufficientStock(cartQuantity: number, availableStock?: number): boolean {
    if (availableStock === undefined) {
      // If stock info is not available, assume it's valid (don't block update)
      return true;
    }
    return availableStock >= cartQuantity;
  }

  /**
   * Update prices for cart items
   */
  async updatePrices(
    cartItems: Array<{ 
      sku: string; 
      id?: string;
      price?: number; 
      sale_price: number;
      promotional_pack_unit_price?: number;
      promotional_pack_quantity?: number;
      product_prices?: any[];
      quantity?: number;
      stock?: number;
    }>,
    options: CartPriceUpdateOptions = {}
  ): Promise<PriceUpdateResult> {
    const { forceUpdate = false, silent = false, onProgress } = options;
    
    if (cartItems.length === 0) {
      return { success: true, updatedItems: [], errors: [] };
    }

    const skus = [...new Set(cartItems.map(item => item.sku || item.id))];
    
    if (!this.needsUpdate(skus, forceUpdate)) {
      return { success: true, updatedItems: [], errors: [] };
    }

    try {
      if (!silent) {
        console.log(`Updating prices for ${skus.length} products...`);
      }

      const freshPrices = await getFreshProductPrices(skus);
      const now = Date.now();
      skus.forEach(sku => this.lastUpdateTime.set(sku, now));

      if (onProgress) {
        onProgress(50);
      }

      const updatedItems: PriceUpdateResult['updatedItems'] = [];
      const errors: string[] = [];

      for (const freshPrice of freshPrices) {
        const cartItem = cartItems.find(item => (item.sku || item.id) === freshPrice.id);
        
        if (cartItem) {
          const cartQuantity = cartItem.quantity || 1;
          const availableStock = freshPrice.quantity || freshPrice.in_stock || cartItem.stock;
          
          // Validate stock availability
          if (!this.hasSufficientStock(cartQuantity, availableStock)) {
            if (!silent) {
              errors.push(`Insufficient stock for product ${freshPrice.id}. Available: ${availableStock}, Required: ${cartQuantity}`);
            }
            continue; // Skip this item, don't update prices if stock is insufficient
          }

          const hasPriceChange = 
            cartItem.price !== freshPrice.price ||
            cartItem.sale_price !== freshPrice.sale_price;
          
          // Check for promotional pack price change (for product kits)
          const hasPromotionalPackPriceChange = 
            freshPrice.promotional_pack_unit_price !== undefined &&
            cartItem.promotional_pack_unit_price !== freshPrice.promotional_pack_unit_price;

          // If promotional pack price changed, validate kit promotion requirements
          if (hasPromotionalPackPriceChange) {
            const promotionalPackQuantity = cartItem.promotional_pack_quantity;
            
            // Only update promotional pack price if the promotion is valid
            if (!this.isValidKitPromotion(cartQuantity, promotionalPackQuantity, availableStock)) {
              if (!silent) {
                console.log(`Skipping promotional pack price update for ${freshPrice.id}: promotion requirements not met (quantity: ${cartQuantity}, required: ${promotionalPackQuantity})`);
              }
              // Still update regular prices if they changed, but skip promotional pack price
              if (hasPriceChange) {
                updatedItems.push({
                  id: freshPrice.id,
                  price: freshPrice.price,
                  sale_price: freshPrice.sale_price,
                  oldPrice: cartItem.price,
                  oldSalePrice: cartItem.sale_price,
                  product_prices: freshPrice.product_prices,
                });
              }
              continue;
            }
          }

          if (hasPriceChange || hasPromotionalPackPriceChange) {
            const updateItem: PriceUpdateResult['updatedItems'][0] = {
              id: freshPrice.id,
              price: freshPrice.price,
              sale_price: freshPrice.sale_price,
              oldPrice: cartItem.price,
              oldSalePrice: cartItem.sale_price,
            };

            // Include promotional pack price update if it changed and promotion is valid
            if (hasPromotionalPackPriceChange && freshPrice.promotional_pack_unit_price !== undefined) {
              updateItem.promotional_pack_unit_price = freshPrice.promotional_pack_unit_price;
              updateItem.oldPromotionalPackUnitPrice = cartItem.promotional_pack_unit_price;
            }

            // Include product_prices array if available (needed for updating cart item)
            if (freshPrice.product_prices) {
              updateItem.product_prices = freshPrice.product_prices;
            }

            updatedItems.push(updateItem);
          }
        }
      }

      if (onProgress) {
        onProgress(100);
      }

      if (!silent && updatedItems.length > 0) {
        console.log(`Updated prices for ${updatedItems.length} products`);
      }

      return {
        success: true,
        updatedItems,
        errors,
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to update cart prices:', error);
      
      return {
        success: false,
        updatedItems: [],
        errors: [errorMessage],
      };
    }
  }

  /**
   * Get the last update time for a specific SKU
   */
  getLastUpdateTime(sku: string): number | undefined {
    return this.lastUpdateTime.get(sku);
  }

  /**
   * Clear the update cooldown for specific SKUs (useful for testing or force updates)
   */
  clearCooldown(skus: string[]): void {
    skus.forEach(sku => this.lastUpdateTime.delete(sku));
  }

  /**
   * Set a custom cooldown period
   */
  setCooldown(ms: number): void {
    this.updateCooldown = ms;
  }
}

export const cartPriceUpdateService = CartPriceUpdateService.getInstance();

